import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import { ArticleBody, Section } from "@/components/guide-page";
import { absUrl } from "@/lib/site";

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { title: "Disclaimer | LoneStarMap" },
      {
        name: "description",
        content:
          "LoneStarMap provides educational information only. It does not provide medical advice, diagnosis, or treatment.",
      },
      { property: "og:title", content: "Disclaimer" },
      { property: "og:description", content: "Educational information only, not medical advice." },
      { property: "og:url", content: absUrl("/disclaimer") },
    ],
    links: [{ rel: "canonical", href: absUrl("/disclaimer") }],
  }),
  component: DisclaimerPage,
});

const BODY = "measure text-base leading-[1.7] text-foreground/85";

function DisclaimerPage() {
  return (
    <PageShell
      title="Disclaimer"
      intro="LoneStarMap provides educational information about ticks, tick-bite prevention, and alpha-gal syndrome awareness. It does not provide medical advice, diagnosis, or treatment, and using the Site does not create a doctor–patient relationship. Always consult a licensed healthcare professional with medical questions."
    >
      <ArticleBody>
        <aside
          aria-labelledby="not-for-emergencies"
          className="border border-l-[3px] border-border border-l-destructive bg-card px-5 py-4"
        >
          <h2 id="not-for-emergencies" className="label-caps text-destructive">
            Not for emergencies
          </h2>
          <p className="measure mt-1.5 text-base leading-relaxed text-foreground">
            If you are having a severe allergic reaction, trouble breathing, or another medical
            emergency, call 911 (or your local emergency number) or contact Poison Control
            immediately. Do not rely on this Site.
          </p>
        </aside>

        <Section title="Risk estimates">
          <p className={BODY}>
            Risk previews shown on this site are educational estimates based on publicly reported
            tick surveillance information. They should not replace official public health guidance
            from agencies like the CDC or your state health department.
          </p>
        </Section>
        <Section title="No warranty">
          <p className={BODY}>
            Information on LoneStarMap is provided &ldquo;as is&rdquo; without warranty of any kind.
            We make no guarantees about completeness or accuracy for any specific location or
            individual.
          </p>
        </Section>
        <Section title="Not official surveillance">
          <p className={BODY}>
            LoneStarMap does not run tick surveillance and does not collect tick sightings from the
            public. The levels on our map and state pages are our own editorial summary of published
            CDC and peer-reviewed distribution data, listed on the{" "}
            <Link to="/sources" className="link">
              sources page
            </Link>
            . They are not a substitute for CDC or state health department surveillance and should
            not be used to make medical decisions.
          </p>
          <p className={BODY}>
            Where public data for an area is thin, we say so rather than estimating. A
            &ldquo;limited public data&rdquo; label means exactly that, not that ticks are absent.
          </p>
        </Section>
        <Section title="Limitation of liability">
          <p className={BODY}>
            To the fullest extent permitted by law, LoneStarMap and its operators are not liable for
            any decisions, actions, injuries, illnesses, or damages arising from use of this site or
            reliance on its information. Always seek professional medical guidance for medical
            questions and defer to official public health authorities for surveillance data.
          </p>
        </Section>
      </ArticleBody>
    </PageShell>
  );
}
